import * as React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { getSchoolYearsList } from '../../common/+store/utils';
import {
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Icon,
  Grid,
  IconButton,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { AssessmentTypes } from '..';
import DeleteModal from '../DeleteModal';
import FileReadiness from './FileReadiness.component';

const isDemo = process.env.REACT_APP_THEME === 'demo';

export const styles = (theme) => ({
  // Plain, top-aligned wrapper for the create/edit form. (Previously this reused a
  // file-dropzone style — height:100% + centered flex — which floated the form in the
  // middle of a full-height box, leaving large empty space above and below it.)
  formContainer: {
    border: isDemo ? '1px solid #C4C4C4' : '1px solid #656565',
    borderRadius: '8px',
    padding: theme.spacing.unit * 2,
    marginTop: theme.spacing.unit,
  },
  tableWrapper: {
    marginBottom: '24px',
    background: 'unset',
  },
  headerGrid: {
    alignItems: 'center',
  },
  button: {
    marginBottom: theme.spacing.unit,
    marginTop: theme.spacing.unit,
  },
  rightIcon: {
    marginLeft: theme.spacing.unit,
  },
  leftIcon: {
    marginRight: theme.spacing.unit,
  },
  form: {
    width: '95%',
    padding: theme.spacing.unit * 3,
  },
  tableCell: {
    padding: isDemo ? '3px 8px' : '4px 16px',
    textTransform: 'capitalize',
  },
  tableCellOption: {
    padding: 0,
  },
  // Edit/Delete columns: shrink to just past the header label, minimal padding so the
  // column is no wider than the "Edit"/"Delete" text itself.
  actionHeader: {
    padding: '4px 8px',
    width: 56,
    whiteSpace: 'nowrap',
  },
  // Keep single-value cells (group name, owner) on one line so they take only the width
  // they need and don't claim extra rows.
  nowrapCell: {
    whiteSpace: 'nowrap',
  },
  // Give the Assessments column the surplus table width and keep each test name on a
  // single line, so a multi-set group no longer wraps into a very tall row.
  assessmentsCell: {
    width: '99%',
    whiteSpace: 'nowrap',
  },
});

// State (STAAR/TELPAS/EOC) sets and interim sets are loaded by two different server rails —
// state sets are queried by type, interims by set id — so a group holding both returns an
// empty report instead of an error. The picker keeps a group to one kind or the other.
const INTERIM_TYPES = [AssessmentTypes.Interim, AssessmentTypes.ComboAssessment, AssessmentTypes.DMACInterim];

// Which test counts when one student sat several in the same subject. Saved on the group so
// a set of sequential CBAs keeps averaging every time it is opened, instead of silently
// reverting to Best — Best across assessments covering different material is misleading.
// The three values are the server's optionType strings verbatim
// (functions/modules/Handlers/httpsAToFAndFlightPlan/utils.js) and the same values the
// report's own Options dropdown uses; do not rename them.
const DEFAULT_SCORE_OPTION = 'best';
const SCORE_OPTIONS = [
  { value: 'best', label: 'Best' },
  { value: 'average', label: 'Average' },
  { value: 'mostRecent', label: 'Most Recent' },
];

// Groups saved before this setting existed carry no value and run as Best, so show Best
// rather than a blank — the label states what the report will actually do.
const scoreOptionLabel = (scoreOption) => {
  const match = SCORE_OPTIONS.find((o) => o.value === (scoreOption || DEFAULT_SCORE_OPTION));
  return match ? match.label : scoreOptionLabel(DEFAULT_SCORE_OPTION);
};

const AssessmentOption = (props) => {
  const { option, setFieldValue, field, assessments, disabled } = props;
  const handleChange = (e) => {
    if (e.target.checked) {
      setFieldValue(field, [...assessments, option.value]);
    } else {
      setFieldValue(
        field,
        assessments.filter((e) => e !== option.value)
      );
    }
  };
  return (
    <MenuItem key={option.value} value={option.value} disabled={disabled}>
      <Checkbox
        style={{ color: disabled ? undefined : '#60b9c5' }}
        checked={assessments.includes(option.value)}
        onChange={handleChange}
        disabled={disabled}
      />
      <ListItemText primary={option.label} />
    </MenuItem>
  );
};

// Convert ending year (e.g., "2025") to school year format (e.g., "2024-2025")
const endingYearToSchoolYear = (endingYear) => {
  if (!endingYear) return null;
  const year = parseInt(endingYear, 10);
  if (isNaN(year)) return null;
  return `${year - 1}-${year}`;
};

class AssessmentGroupManager extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    loading: PropTypes.bool.isRequired,
    profile: PropTypes.object,
    needsInit: PropTypes.bool.isRequired,
    assessmentList: PropTypes.array.isRequired,
    onCreateAssessmentGroupClick: PropTypes.func.isRequired,
    getAssessmentGroups: PropTypes.func.isRequired,
    initChecklist: PropTypes.func.isRequired,
    rawCheckList: PropTypes.object,
    assessmentGroups: PropTypes.object.isRequired,
    assessmentGroupCreateOpen: PropTypes.bool.isRequired,
    handleAddAssessmentGroup: PropTypes.func.isRequired,
    handleEditAssessmentGroup: PropTypes.func.isRequired,
    handleGroupDelete: PropTypes.func.isRequired,
    canDelete: PropTypes.func.isRequired,
    renderOwner: PropTypes.func.isRequired,
    onAssessmentGroupVisibilityBySuperAdminOnlyChange: PropTypes.func.isRequired,
    selectedYears: PropTypes.object, // Set of school years like "2024-2025"
  };

  state = {
    errorMsgs: [],
    isDeleteModalOpen: false,
    groupId: null,
  };

  componentDidMount() {
    this.shouldInit();
  }

  componentDidUpdate(prevProps, prevState) {
    const { assessmentGroups, assessmentGroupCreateOpen } = this.props;
    this.shouldInit();
    if (
      (prevState.isDeleteModalOpen && !(prevState.groupId in assessmentGroups)) ||
      (prevProps.assessmentGroupCreateOpen && !assessmentGroupCreateOpen)
    ) {
      this.setState({
        isDeleteModalOpen: false,
        groupId: null,
      });
    }
  }

  deleteIcon = (assessmentGroupId, assessmentGroup) => {
    const { canDelete } = this.props;
    const isActive = canDelete(assessmentGroup.owner);
    return (
      <IconButton style={{ padding: 6 }} onClick={this.deleteButtonClicked(assessmentGroupId)} disabled={!isActive}>
        <DeleteIcon color={isActive ? 'error' : 'disabled'} />
      </IconButton>
    );
  };

  editIcon = (assessmentGroupId, assessmentGroup) => {
    const { canDelete } = this.props;
    const isActive = canDelete(assessmentGroup.owner);
    return (
      <IconButton style={{ padding: 6 }} onClick={this.editButtonClicked(assessmentGroupId)} disabled={!isActive}>
        <EditIcon color={isActive ? 'primary' : 'disabled'} />
      </IconButton>
    );
  };

  deleteButtonClicked = (groupId) => () => {
    this.setState({
      isDeleteModalOpen: true,
      groupId,
    });
  };

  editButtonClicked = (groupId) => () => {
    this.props.onCreateAssessmentGroupClick();
    this.setState({ groupId });
  };

  handleDelete = (assessmentGroup) => () => {
    const { profile, handleGroupDelete } = this.props;
    handleGroupDelete(profile, assessmentGroup);
  };

  cancelDelete = () => {
    this.setState({
      isDeleteModalOpen: false,
      groupId: null,
    });
  };

  // Interims carry an administration date rather than a year-stamped type, so they belong to
  // the school year whose July-to-July window contains that date. Bounds are half-open so a
  // file dated exactly July 1 lands in the year that starts that day instead of in neither.
  getInterimAssessment = (assessmentList, startDate, endDate) =>
    assessmentList
      .filter((a) => {
        if (!INTERIM_TYPES.includes(a.type) || !a.date) {
          return false;
        }
        const date = moment(a.date);
        return date.isValid() && date.isSameOrAfter(moment(startDate)) && date.isBefore(moment(endDate));
      })
      .map((source) => ({
        value: source.id,
        label: `${source.name} (${moment(source.date).format('MMM D, YYYY')})`,
      }));

  getStateAssessments = (assessmentList, schoolYear) =>
    assessmentList
      .filter((a) =>
        [
          AssessmentTypes.STAAR + schoolYear,
          AssessmentTypes.STAARALT + schoolYear,
          AssessmentTypes.TELPAS + schoolYear,
          AssessmentTypes.TELPASALT + schoolYear,
          AssessmentTypes.EOC + schoolYear,
          AssessmentTypes.EOCALT + schoolYear,
        ].includes(a.type)
      )
      .map((source) => ({
        value: source.id,
        label: source.name,
      }));

  // Returns the two kinds separately so the form can label them and stop a group from mixing
  // them. Interims were dropped from this picker in June 2025; they are back because grouping
  // several benchmarks/CBAs into one cumulative view is the point of a custom group.
  getYearAssessmentSources = (schoolYear) => {
    // Super-admin-hidden sets stay out of the picker. Ids already saved on a
    // group survive an edit because Formik keeps unrendered values as-is.
    const assessmentList = this.props.assessmentList.filter((a) => !a.hiddenFromUser);
    return {
      state: this.getStateAssessments(assessmentList, schoolYear),
      interim: this.getInterimAssessment(assessmentList, `${schoolYear - 1}-07-01`, `${schoolYear}-07-01`),
    };
  };

  // 'interim' | 'state' | null (nothing picked yet, so both sections stay open).
  getSelectionKind = (assessments) => {
    const { assessmentList } = this.props;
    if (!assessments || !assessments.length) {
      return null;
    }
    const isInterim = assessments.some((id) => {
      const assessment = assessmentList.find((a) => a.id === id);
      return !!assessment && INTERIM_TYPES.includes(assessment.type);
    });
    return isInterim ? 'interim' : 'state';
  };

  // A-F File Readiness answers "does this year have the TEA files A-F needs", which is only
  // meaningful for the state group — the once-a-year set the auto-grouper maintains. An
  // interim group pools CBAs and benchmarks that A-F never scores, so its cell stays blank
  // rather than reporting a readiness that has nothing to do with it.
  //
  // Groups saved before the kind stamp existed carry no kind, so fall back to their members'
  // types. An empty group has no members to judge and reads as state, which is right: the
  // only groups that sit empty are auto-created A-F ones waiting on their first upload.
  isStateGroup = (assessmentGroup) => {
    if (!assessmentGroup) {
      return false;
    }
    const kind = assessmentGroup.kind || this.getSelectionKind(assessmentGroup.assessments);
    return kind !== 'interim';
  };

  shouldInit() {
    const { profile, needsInit, loading, getAssessmentGroups, initChecklist, rawCheckList } = this.props;
    if (profile && needsInit && !loading) {
      getAssessmentGroups(profile);
    }
    if (profile && rawCheckList == null && !this.checklistRequested) {
      this.checklistRequested = true;
      initChecklist(profile);
    }
  }

  handleAssessmentGroupVisibilityBySuperAdminOnlyChange = async (assessmentGroup, assessmentGroupId, isChecked) => {
    const { profile, onAssessmentGroupVisibilityBySuperAdminOnlyChange } = this.props;
    try {
      assessmentGroup.id = assessmentGroupId;
      assessmentGroup.isVisibleBySuperAdminOnly = isChecked;
      onAssessmentGroupVisibilityBySuperAdminOnlyChange(profile, assessmentGroup);
    } catch (error) {
      console.error('Error on change assessment visibility', error);
    }
  };

  renderField = (label, fieldName, helperText = null, options = null, disabled) => (
    <Grid container spacing={24} key={fieldName}>
      <Grid key={fieldName} container direction="column" justify="space-between" alignItems="flex-start" item>
        {options ? (
          <Field
            name={fieldName}
            render={({ field, form: { errors, touched } }) => (
              <TextField
                {...field}
                disabled={disabled}
                select
                label={label}
                helperText={!!errors[fieldName] && touched[fieldName] ? null : helperText}
                margin="normal"
                variant="outlined"
                error={!!errors[fieldName] && touched[fieldName]}
              >
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        ) : (
          <Field
            key={fieldName}
            name={fieldName}
            render={({ field, form: { errors, touched } }) => (
              <TextField
                variant="outlined"
                {...field}
                disabled={disabled}
                id={fieldName}
                label={label}
                margin="normal"
                helperText={!!errors[fieldName] && touched[fieldName] ? null : helperText}
                error={!!errors[fieldName] && touched[fieldName]}
              />
            )}
          />
        )}
        <Typography variant="caption" gutterBottom color="error" align="left" style={{ paddingRight: '16px' }}>
          <ErrorMessage key={`err${fieldName}`} name={fieldName} />
        </Typography>
      </Grid>
    </Grid>
  );

  renderAssessmentsList = (values, setFieldValue) => {
    const sources = this.getYearAssessmentSources(values.year);
    const selectionKind = this.getSelectionKind(values.assessments);
    const sections = [
      { kind: 'state', heading: 'State Assessments', options: sources.state },
      { kind: 'interim', heading: 'Interim & Local Assessments', options: sources.interim },
    ].filter((section) => section.options.length);

    return (
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" gutterBottom>
          Assessments List
        </Typography>
        {sections.map((section) => {
          const disabled = !!selectionKind && selectionKind !== section.kind;
          return (
            <React.Fragment key={section.kind}>
              <Typography variant="caption" align="left" style={{ marginTop: 8 }}>
                {section.heading}
              </Typography>
              {section.options.map((option) => (
                <AssessmentOption
                  key={option.value}
                  option={option}
                  setFieldValue={setFieldValue}
                  field="assessments"
                  assessments={values.assessments}
                  disabled={disabled}
                />
              ))}
            </React.Fragment>
          );
        })}
        {sections.length === 0 && (
          <Typography variant="caption" gutterBottom color="error" align="left" style={{ paddingRight: '16px' }}>
            {`None available for ${values.year}.`}
          </Typography>
        )}
        {sections.length > 1 && (
          <Typography variant="caption" gutterBottom align="left" style={{ paddingRight: '16px', display: 'block' }}>
            A group holds either state assessments or interim assessments, not both — the two are scored on different
            rails.
          </Typography>
        )}
        <Typography variant="caption" gutterBottom color="error" align="left" style={{ paddingRight: '16px' }}>
          <ErrorMessage key="errAssessmentList" name="assessments" />
        </Typography>
      </Grid>
    );
  };

  renderCreateGroupModal = () => {
    const {
      classes,
      onCloseAssessmentGroupClick,
      handleAddAssessmentGroup,
      profile,
      assessmentGroups,
      handleEditAssessmentGroup,
    } = this.props;
    const { groupId } = this.state;
    const currentGroup = groupId in assessmentGroups ? assessmentGroups[groupId] : null;
    const currentGroupName = currentGroup ? currentGroup.name : null;
    const groupNames = Object.values(assessmentGroups)
      .map((group) => group.name.trim())
      .filter((name) => name !== currentGroupName);
    // Spread rather than hand Formik the redux object by reference. `assessments` is
    // defaulted because RTDB drops empty arrays: a group saved with no members comes back
    // with no `assessments` key at all, and the option list calls .includes() on it. The
    // auto-created A-F group starts empty, so that path is reachable.
    const initialValues = currentGroup
      ? {
          ...currentGroup,
          assessments: currentGroup.assessments || [],
          scoreOption: currentGroup.scoreOption || DEFAULT_SCORE_OPTION,
        }
      : {
          year: '',
          name: '',
          assessments: [],
          scoreOption: DEFAULT_SCORE_OPTION,
        };

    const AssessmentGroupSchema = Yup.object().shape({
      name: Yup.string()
        .trim()
        .required('This field is required.')
        .notOneOf(groupNames, 'An assessment group with this name already exists.'),
      year: Yup.string().required('This field is required.'),
      assessments: Yup.array().required('Select at least one assessment.'),
    });
    const schoolYears = getSchoolYearsList();

    // const schoolYears = [
    //   {
    //     value: '2019',
    //     label: '2018-2019',
    //   },
    //   {
    //     value: '2020',
    //     label: '2019-2020',
    //   },
    //   {
    //     value: '2021',
    //     label: '2020-2021',
    //   },
    //   {
    //     value: '2022',
    //     label: '2021-2022',
    //   },
    // ];

    return (
      <React.Fragment>
        <Button onClick={onCloseAssessmentGroupClick}>
          <Icon className={classes.leftIcon}> chevron_left </Icon>
          Back
        </Button>
        <div className={classes.formContainer}>
          <Formik
            initialValues={initialValues}
            validationSchema={AssessmentGroupSchema}
            onSubmit={(values) => {
              // Stamped so the server-side auto-grouper can tell an interim group apart from
              // a district's A-F group and never drops a state file into it.
              const kind = this.getSelectionKind(values.assessments) || 'state';
              const group = Object.assign({}, values, {
                kind,
                // Never write undefined — firebase push() throws on it. Only interim groups
                // carry a score option; the state rail ignores it, so storing one there
                // would imply a setting that does nothing.
                scoreOption: kind === 'interim' ? values.scoreOption || DEFAULT_SCORE_OPTION : null,
              });
              return groupId
                ? handleEditAssessmentGroup(profile, groupId, group)
                : handleAddAssessmentGroup(profile, group);
            }}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form className={classes.form}>
                <Grid container alignItems="flex-start" direction="row">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="button" gutterBottom>
                      {`${groupId ? 'Update' : 'Create'} Assessments Group`}
                    </Typography>
                    {this.renderField('Year', 'year', 'The Assessment Group Year', schoolYears, !!groupId)}
                    {values.year.length
                      ? this.renderField('Group Name', 'name', 'A Unique Assessment Group Name')
                      : null}
                    {/* Only for interim groups: the state rail scores by type and ignores
                        this entirely, so offering it there would be a dead control. */}
                    {values.year.length && this.getSelectionKind(values.assessments) === 'interim'
                      ? this.renderField(
                          'Score Option',
                          'scoreOption',
                          'Which test counts when a student sat more than one in the same subject',
                          SCORE_OPTIONS
                        )
                      : null}
                    {values.year.length ? (
                      <Button type="submit" variant="contained" color="primary" className={classes.button}>
                        {groupId ? 'Update Group' : 'Create Group'}
                      </Button>
                    ) : null}
                  </Grid>
                  {values.year.length ? this.renderAssessmentsList(values, setFieldValue) : null}
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </React.Fragment>
    );
  };

  isGroupInSelectedYear = (assessmentGroup) => {
    const { selectedYears } = this.props;
    if (!selectedYears || selectedYears.size === 0) return true; // Show all if none selected
    const schoolYear = endingYearToSchoolYear(assessmentGroup.year);
    return schoolYear && selectedYears.has(schoolYear);
  };

  renderAssessmentGroups = () => {
    const {
      profile,
      classes,
      assessmentGroups,
      onCreateAssessmentGroupClick,
      assessmentList,
      renderOwner,
      rawCheckList,
    } = this.props;
    const groups = Object.entries(assessmentGroups).filter(([, group]) => this.isGroupInSelectedYear(group));

    return (
      <React.Fragment>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
          <Button variant="contained" color="primary" className={classes.button} onClick={onCreateAssessmentGroupClick}>
            Create Assessment Group <Icon className={classes.rightIcon}> playlist_add </Icon>
          </Button>
        </div>
        {groups.length ? (
          <Paper className={classes.tableWrapper}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.actionHeader} align="center">
                    Edit
                  </TableCell>
                  <TableCell className={classes.actionHeader} align="center">
                    Delete
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`} align="left">
                    Group Name
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`} align="left">
                    Created By
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.assessmentsCell}`} align="left">
                    Assessments
                  </TableCell>
                  <TableCell className={classes.tableCell} align="left">
                    Year
                  </TableCell>
                  <TableCell className={classes.tableCell} align="left">
                    Details
                  </TableCell>
                  {profile && (profile.type == 'superAdmin' || profile.isSuperAdmin == true) && (
                    <TableCell className={classes.tableCell} align="center">
                      Super Admin Only
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(([firebaseId, assessmentGroup]) => {
                  let assessmentListNames = [];
                  if (assessmentGroup && assessmentGroup.assessments) {
                    assessmentListNames = assessmentGroup.assessments
                      .map((assessmentId) => {
                        const curAssessment = assessmentList.find((a) => a.id === assessmentId);
                        return curAssessment ? curAssessment.name : null;
                      })
                      .filter((a) => !!a);
                  }
                  return (
                    <TableRow key={firebaseId}>
                      <TableCell className={classes.tableCellOption} align="center">
                        {this.editIcon(firebaseId, assessmentGroup)}
                      </TableCell>
                      <TableCell className={classes.tableCellOption} align="center">
                        {this.deleteIcon(firebaseId, assessmentGroup)}
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`} align="left">
                        <Typography> {assessmentGroup.name} </Typography>
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`} align="left">
                        <Typography> {renderOwner(assessmentGroup.owner)} </Typography>
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.assessmentsCell}`} align="left">
                        {assessmentListNames.length ? (
                          assessmentListNames.map((a, index) => <Typography key={index}> {a} </Typography>)
                        ) : (
                          <Typography> - </Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell} align="left">
                        <Typography> {assessmentGroup.year} </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="left">
                        {this.isStateGroup(assessmentGroup) ? (
                          <FileReadiness yearNode={rawCheckList ? rawCheckList[assessmentGroup.year] : null} />
                        ) : (
                          <Typography>{`Score: ${scoreOptionLabel(assessmentGroup.scoreOption)}`}</Typography>
                        )}
                      </TableCell>

                      {profile && (profile.type == 'superAdmin' || profile.isSuperAdmin == true) && (
                        <TableCell className={classes.tableCell} align="center">
                          <Typography>
                            <Checkbox
                              checked={assessmentGroup.isVisibleBySuperAdminOnly == true}
                              onChange={(e) =>
                                this.handleAssessmentGroupVisibilityBySuperAdminOnlyChange(
                                  assessmentGroup,
                                  firebaseId,
                                  e.target.checked
                                )
                              }
                              color="primary"
                            />
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        ) : (
          <Typography> No Assessment Groups Created Yet! </Typography>
        )}
      </React.Fragment>
    );
  };

  render() {
    const { assessmentGroupCreateOpen, assessmentGroups } = this.props;
    const { groupId, isDeleteModalOpen } = this.state;
    return (
      <React.Fragment>
        <DeleteModal
          open={isDeleteModalOpen}
          assessment={groupId ? assessmentGroups[groupId] : null}
          cancelDelete={this.cancelDelete}
          doDelete={this.handleDelete}
          groupId={groupId}
          isGroup
        />
        {assessmentGroupCreateOpen ? this.renderCreateGroupModal() : this.renderAssessmentGroups()}
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(AssessmentGroupManager);
